import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { useOrderEfficiency } from '../hooks/useReports';
import { exportToCSV } from '@/shared/utils/exportUtils';
import { formatDate } from '@/shared/utils/dateUtils';
import type { OrderEfficiencyFilters } from '../services/reportsService';

interface OrderEfficiencySectionProps {
  filters: OrderEfficiencyFilters;
}

export function OrderEfficiencySection({ filters }: OrderEfficiencySectionProps) {
  const { data, isLoading, error } = useOrderEfficiency(filters);

  const handleExport = () => {
    if (!data) return;

    exportToCSV(
      data.map((item) => ({
        'PO Number': item.po_number,
        'Ordered Date': item.ordered_date,
        Manufacturer: item.manufacturer_name,
        Location: item.location_name,
        'Frame Count': item.frame_count,
        'Min Order Qty': item.min_order_qty || '',
        'Meets Min Qty': item.meets_min_qty ? 'Yes' : 'No',
        Status: item.status,
      })),
      'order-efficiency-report'
    );
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Order Efficiency</h2>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Order Efficiency</h2>
        <div className="text-center py-8 text-red-600">
          Error loading data: {error.message}
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Order Efficiency</h2>
        <div className="text-center py-8 text-gray-500">
          No orders found in the selected date range
        </div>
      </Card>
    );
  }

  const belowMinCount = data.filter((item) => item.status === 'BELOW_MIN').length;
  const meetsMinCount = data.filter((item) => item.status === 'MEETS_MIN').length;
  const optimalCount = data.filter((item) => item.status === 'OPTIMAL').length;
  const totalOrders = data.length;
  const efficiencyRate = totalOrders > 0 
    ? ((meetsMinCount + optimalCount) / totalOrders * 100).toFixed(1)
    : '0.0';

  const getStatusBadge = (status: 'OPTIMAL' | 'MEETS_MIN' | 'BELOW_MIN') => {
    switch (status) {
      case 'OPTIMAL':
        return <Badge variant="success">Optimal</Badge>;
      case 'MEETS_MIN':
        return <Badge variant="success">Meets Minimum</Badge>;
      case 'BELOW_MIN':
        return <Badge variant="danger">Below Minimum</Badge>;
    }
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Order Efficiency</h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing data from {formatDate(filters.date_from)} to{' '}
            {formatDate(filters.date_to)}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border-2">
          <div className="text-sm text-gray-600">Total Orders</div>
          <div className="text-2xl font-bold">{totalOrders}</div>
        </Card>
        <Card className="p-4 border-2 border-green-200 bg-green-50">
          <div className="text-sm text-gray-600">Meets Minimum</div>
          <div className="text-2xl font-bold text-green-600">{meetsMinCount + optimalCount}</div>
        </Card>
        <Card className="p-4 border-2 border-red-200 bg-red-50">
          <div className="text-sm text-gray-600">Below Minimum</div>
          <div className="text-2xl font-bold text-red-600">{belowMinCount}</div>
        </Card>
        <Card className="p-4 border-2">
          <div className="text-sm text-gray-600">Efficiency Rate</div>
          <div className="text-2xl font-bold">{efficiencyRate}%</div>
        </Card>
      </div>

      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> This report analyzes whether orders meet manufacturer minimum order quantities. 
          Orders are grouped by PO number, manufacturer, and location. Free shipping analysis requires order amount 
          data which is not currently tracked in the system.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                PO Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ordered Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Manufacturer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Frame Count
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Min Required
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => (
              <tr
                key={`${item.po_number}-${item.manufacturer_id}-${item.location_id}-${index}`}
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap font-medium">
                  {item.po_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {formatDate(item.ordered_date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap font-medium">
                  {item.manufacturer_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <span className="hidden md:inline">{item.location_name}</span>
                  <span className="md:hidden">
                    {item.location_short_code || item.location_name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                  {item.frame_count}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500">
                  {item.min_order_qty !== null ? item.min_order_qty : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  {getStatusBadge(item.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-500">
        Showing {data.length} order{data.length !== 1 ? 's' : ''}
      </div>
    </Card>
  );
}


